import React, { useCallback, useMemo } from "react";
import { Box } from "@chakra-ui/react";
import ja from "date-fns/locale/ja";
import ReactDatePicker from "react-datepicker";
import { PickerMode, PICKER_MODE } from "../constants";

interface CalendarPopoverContentProps {
  mode: PickerMode;
  selectedValue: Date | null | undefined;
  pickerFormat: string;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  onChange: (date: Date) => void;
}

const CalendarPopoverContentComponent: React.FC<CalendarPopoverContentProps> = ({
  mode,
  selectedValue,
  pickerFormat,
  inputRef,
  onChange,
}) => {
  const dayClassName = useCallback(
    (value: Date) => {
      if (selectedValue) return "";

      const today = new Date();
      const isToday =
        value.getDate() === today.getDate() &&
        value.getMonth() === today.getMonth() &&
        value.getFullYear() === today.getFullYear();

      return isToday ? "react-datepicker__day--today react-datepicker__day--selected" : "";
    },
    [selectedValue]
  );

  const modeProps = useMemo(
    () =>
      mode === PICKER_MODE.DATE
        ? {
            dateFormat: pickerFormat,
            dayClassName: dayClassName,
          }
        : {
            timeCaption: "時間",
            timeIntervals: 1,
            showTimeSelect: true,
            showTimeSelectOnly: true,
            timeFormat: pickerFormat,
          },
    [mode, pickerFormat, dayClassName]
  );

  const handleCalendarOpen = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const handleChange = useCallback(
    (d: Date | null) => {
      if (d) {
        inputRef.current?.focus();
        onChange(d);
      }
    },
    [inputRef, onChange]
  );

  return (
    <Box
      zIndex={999}
      tabIndex={0}
      className="wrapper-react-datepicker"
      sx={{
        ".react-datepicker": {
          fontSize: "initial !important",
        },
      }}
    >
      <ReactDatePicker
        inline
        open={false}
        selected={selectedValue}
        locale={ja}
        tabIndex={0}
        onCalendarOpen={handleCalendarOpen}
        {...modeProps}
        onChange={handleChange}
      />
    </Box>
  );
};

export const CalendarPopoverContent = React.memo(CalendarPopoverContentComponent);
